import axios from 'axios'
import router from 'next/router'
import sendToLog from 'helpers/send-log'
import { getBrowser } from 'helpers/user-agent'
import { getFingerprint } from 'helpers/user-device'
import { getLocalStorage } from 'helpers/local-storage'
import { setSession } from 'utils/jwt'
import appConfig from 'configs/app'
import authConfig from 'configs/auth'
import { API_USER } from 'routes/apis'
import isEmpty from 'helpers/is-empty'
import redirect from 'helpers/redirect'
import { forceDeleteCookies } from 'helpers/cookie'
import { getRequestID } from 'helpers/log'
import { getSessionStorage } from 'helpers/session-storage'
import { getActiveOrgId } from 'helpers/team-access'
import { getActiveLanguage } from 'helpers/locale'
import { useActiveOrgStore } from '~/domains/Organization/store'

const API_TIMEOUT = 20000

const DEFAULT_ERROR_MESSAGE = {
  name: 'client_side_error',
  message:
    'Mohon maaf terjadi kesalahan, silahkan coba beberapa saat lagi.',
  requestId: '',
}

const forceLogout = async () => {
  setSession(null)
  forceDeleteCookies()
  // The reload alone clears in-memory state, but the active org id is
  // persisted to localStorage (this module reads it back below for the
  // Org-Id header), so it survives a reload unless cleared explicitly.
  useActiveOrgStore.getState().clearActiveOrg()
  router.reload()
}
let refreshTokenPromise: Promise<string> | null = null

const getStoredRefreshToken = (): string | null => {
  try {
    const key = authConfig.refreshTokenName || 'refresh_token'
    const raw = getLocalStorage(key)
    if (isEmpty(raw)) return null
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw)
      } catch {
        return raw
      }
    }
    return raw
  } catch {
    return null
  }
}

const apiClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL || '',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Channel: 'WEB',
    'Country-Code': 'ID',
    'Channel-Device': getBrowser(false),
    'Channel-Fingerprint': getFingerprint(),
    'Channel-App-Version': appConfig.publicVersion,
    'Ip-Address': getSessionStorage('ip'),
  },
  timeout: API_TIMEOUT,
})

apiClient.interceptors.request.use((config) => {
  config.headers['Request-Id'] = getRequestID()
  // Tell the BE which language to answer in. Stamped per request rather than in
  // the defaults above: those are evaluated once when this module loads, so a
  // language chosen later — or switched mid-session from the header popover —
  // would never reach the BE. It was pinned to 'id' regardless of the user's
  // choice for exactly that reason.
  config.headers['Language'] = getActiveLanguage()
  // Scope every request to the active org (persisted by the Organization
  // store, read via helpers/team-access). A caller may override it per-request
  // (e.g. the validate-then-commit org switch), so only fill it when unset.
  //
  // Only stamp it when we actually have one. The store is seeded
  // asynchronously (useActiveOrg, once GET /organizations resolves), so
  // requests firing before that would otherwise carry `Org-Id:` with an empty
  // value, which endpoints validating it as a uuid reject with a 400. Skipping
  // it leaves those early requests exactly as they were before this header
  // existed; once an org is known every request carries it as usual.
  if (!config.headers['Org-Id']) {
    const activeOrgId = getActiveOrgId()
    if (activeOrgId) {
      config.headers['Org-Id'] = activeOrgId
    }
  }
  return config
})

apiClient.interceptors.response.use(
  (response) => response,
  async function (error) {
    sendToLog(error)
    const originalRequest = error.config
    const requestId = originalRequest?.headers?.['Request-Id'] || ''

    if (error?.response) {
      if (error.response.status === 401) {
        const isRefreshTokenRequest = Boolean(
          originalRequest?.url?.includes(API_USER.auth.refreshToken),
        )

        // The refresh endpoint's own 401 is handled (and logged out) by the
        // catch inside refreshTokenPromise; rejecting here avoids a double reload.
        if (!originalRequest || isRefreshTokenRequest) {
          if (error.response?.data) {
            error.response.data.requestId = requestId
          }
          return Promise.reject(error)
        }

        if (originalRequest._retry) {
          forceLogout()
          if (error.response?.data) {
            error.response.data.requestId = requestId
          }
          return Promise.reject(error)
        }

        const storedRefreshToken = getStoredRefreshToken()

        // Unauthenticated callers (login, OTP) have no refresh token: a 401
        // there is a plain credential failure, so reject without logging out
        // or reloading the page and let the caller show its error.
        if (isEmpty(storedRefreshToken)) {
          if (error.response?.data) {
            error.response.data.requestId = requestId
          }
          return Promise.reject(error)
        }

        if (error.response.data?.code === 'REFRESH_TOKEN_EXPIRED') {
          forceLogout()
          if (error.response?.data) {
            error.response.data.requestId = requestId
          }
          return Promise.reject(error)
        }

        originalRequest._retry = true

        if (!refreshTokenPromise) {
          refreshTokenPromise = (async () => {
            try {
              const currentRefreshToken = await apiClient.post(
                API_USER.auth.refreshToken,
                {
                  refresh_token: storedRefreshToken,
                },
              )

              if (currentRefreshToken?.data?.code === 'SUCCESS') {
                const { id_token: token } =
                  currentRefreshToken.data.data
                setSession(token)
                return token
              }
              throw new Error('Refresh token unsuccessful')
            } catch (err) {
              forceLogout()
              throw err
            } finally {
              refreshTokenPromise = null
            }
          })()
        }

        try {
          const token = await refreshTokenPromise
          if (!originalRequest.headers) {
            originalRequest.headers = {}
          }
          originalRequest.headers.Authorization = `${token}`
          return apiClient(originalRequest)
        } catch {
          if (error.response?.data) {
            error.response.data.requestId = requestId
          }
          return Promise.reject(error)
        }
      } else if (error.response.status === 503) {
        redirect('/maintenance')
      }

      if (error.response?.data) {
        error.response.data.requestId = requestId
      }

      return Promise.reject(error)
    } else if (error?.code === 'ECONNABORTED') {
      return Promise.reject({
        response: {
          data: {
            code: error?.code,
            message: error?.message,
            requestId,
          },
        },
      })
    }
    return Promise.reject({
      ...DEFAULT_ERROR_MESSAGE,
      message: error.message || DEFAULT_ERROR_MESSAGE.message,
      requestId,
    })
  },
)

export default apiClient
